/**
 * Shipping Calculator — Shopify Horizon Theme
 * File: assets/shipping-calculator.js
 */

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();
    this.provinces = {};
    this._isSelfMutating = false;
    this._eventsBound = false;
  }

  connectedCallback() {
    if (!this._eventsBound) {
      this._bindEvents();
      this._eventsBound = true;
    }

    this._setupMutationObserver();

    setTimeout(() => {
      this._init();
    }, 0);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  _init() {
    this._isSelfMutating = true;
    this._buildProvincesMap();
    this._restoreAddress();
    this._isSelfMutating = false;
  }

  /**
   * Listens for theme cart updates (morphdom / DOM diffing).
   * If the theme morphs the cart section and wipes out the state options,
   * this automatically detects the wipe and rebuilds the state options.
   */
  _setupMutationObserver() {
    if (this._observer) this._observer.disconnect();

    this._observer = new MutationObserver(() => {
      if (this._isSelfMutating) return;

      const countryEl = this.countryEl;
      const provinceEl = this.provinceEl;

      // If a country with states is selected, but the select box lost its options, re-populate!
      if (countryEl && countryEl.value) {
        const code = countryEl.value;
        const expectedProvinces = this.provinces[code];

        if (expectedProvinces && expectedProvinces.length > 0 && (!provinceEl || provinceEl.options.length === 0)) {
          this._init();
        }
      }
    });

    this._observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  // ─── Element getters ───────────────────────────────────────────────────────

  get form()          { return this.querySelector('#shipping-calculator-form-inner'); }
  get results()       { return this.querySelector('#shipping-calculator-results'); }
  get countryEl()     { return this.querySelector('#shipping-calculator-country'); }
  get provinceEl()    { return this.querySelector('#shipping-calculator-province'); }
  get provinceWrap()  { return this.querySelector('#shipping-calculator-province-wrapper'); }
  get submitBtn()     { return this.querySelector('.shipping-calculator__submit'); }
  get submitLabel()   { return this.querySelector('.shipping-calculator__submit-label'); }
  get submitSpinner() { return this.querySelector('.shipping-calculator__submit-spinner'); }
  get zipEl()         { return this.querySelector('#shipping-calculator-zip'); }

  // ─── Events ──────────────────────────────────────────────────────────────

  _bindEvents() {
    this.addEventListener('change', (e) => {
      if (e.target && e.target.id === 'shipping-calculator-country') {
        this._persistPartialAddress({ country: e.target.value, province: '' });
        this._onCountryChange();
      } else if (e.target && e.target.id === 'shipping-calculator-province') {
        this._persistPartialAddress({ province: e.target.value });
      }
    });

    // Self-healing check: if the user clicks into the select box after a cart update, restore states if missing
    this.addEventListener('focusin', (e) => {
      if (e.target && (e.target.id === 'shipping-calculator-country' || e.target.id === 'shipping-calculator-province')) {
        const countryVal = this.countryEl?.value;
        if (countryVal && this.provinces[countryVal]?.length > 0 && this.provinceEl?.options.length === 0) {
          this._init();
        }
      }
    });

    this.addEventListener('input', (e) => {
      if (e.target && e.target.id === 'shipping-calculator-zip') {
        clearTimeout(this._zipSaveTimeout);
        this._zipSaveTimeout = setTimeout(() => {
          this._persistPartialAddress({ zip: e.target.value });
        }, 300);
      }
    });

    this.addEventListener('submit', (e) => {
      if (e.target && e.target.id === 'shipping-calculator-form-inner') {
        e.preventDefault();
        this._calculate();
      }
    });

    // Global listeners for standard Shopify theme cart refresh events
    document.addEventListener('cart:updated', () => this._init());
    document.addEventListener('cart:refresh', () => this._init());
    document.addEventListener('shopify:section:load', () => this._init());
  }

  _persistPartialAddress(patch) {
    let current = {};
    try {
      current = JSON.parse(sessionStorage.getItem('sc_address') || '{}') || {};
    } catch (_) {
      current = {};
    }
    const merged = { ...current, ...patch };
    try {
      sessionStorage.setItem('sc_address', JSON.stringify(merged));
    } catch (_) {}
  }

  // ─── Country / Province handling ─────────────────────────────────────────

  _buildProvincesMap() {
    this.provinces = {};
    if (!this.countryEl) return;

    this.countryEl.querySelectorAll('option').forEach((opt) => {
      const raw = opt.dataset.provinces;
      if (raw) {
        try {
          this.provinces[opt.value] = JSON.parse(raw);
        } catch (_) {}
      }
    });
  }

  _onCountryChange(provinceToSelect) {
    const countryEl = this.countryEl;
    const provinceEl = this.provinceEl;
    const provinceWrap = this.provinceWrap;

    if (!countryEl || !provinceEl || !provinceWrap) return;

    const code = countryEl.value;

    if (!this.provinces || Object.keys(this.provinces).length === 0) {
      this._buildProvincesMap();
    }

    const list = this.provinces[code];

    this._isSelfMutating = true;

    if (list && list.length > 0) {
      provinceEl.innerHTML = '';
      list.forEach(([value, label]) => {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = label;
        provinceEl.appendChild(opt);
      });
      provinceWrap.hidden = false;

      if (provinceToSelect && this._hasOption(provinceEl, provinceToSelect)) {
        provinceEl.value = provinceToSelect;
      }
    } else {
      provinceWrap.hidden = true;
      provinceEl.innerHTML = '';
    }

    this._isSelfMutating = false;
  }

  _hasOption(selectEl, value) {
    return Array.from(selectEl.options).some((opt) => opt.value === value);
  }

  // ─── API call ─────────────────────────────────────────────────────────────

  async _calculate() {
    if (!this.countryEl || !this.provinceEl || !this.zipEl) return;

    const country  = this.countryEl.value.trim();
    const province = this.provinceEl.value.trim();
    const zip      = this.zipEl.value.trim();

    if (!country) {
      this._showError(window.shippingCalcStrings?.countryRequired || 'Please select a country.');
      return;
    }

    this._setLoading(true);
    this._clearResults();
    this._saveAddress({ country, province, zip });

    const params = new URLSearchParams({
      'shipping_address[country]': country,
      'shipping_address[province]': province,
      'shipping_address[zip]': zip,
    });

    try {
      const res = await fetch(`/cart/shipping_rates.json?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });

      if (res.status === 422) {
        const body = await res.json();
        const msgs = Object.entries(body)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join(' ');
        this._showError(msgs || (window.shippingCalcStrings?.invalidAddress || 'Invalid address. Please check your details and try again.'));
        return;
      }

      if (!res.ok) {
        throw new Error(`Unexpected response: ${res.status}`);
      }

      const data = await res.json();
      const rates = data.shipping_rates;

      if (!rates || rates.length === 0) {
        this._showNoRates();
      } else {
        this._showRates(rates);
      }
    } catch (err) {
      console.error('[ShippingCalculator]', err);
      this._showError(
        window.shippingCalcStrings?.generalError ||
        'Unable to fetch shipping rates. Please try again.'
      );
    } finally {
      this._setLoading(false);
    }
  }

  // ─── Render helpers ───────────────────────────────────────────────────────

  _showRates(rates) {
    if (!this.results) return;
    this._isSelfMutating = true;
    const label = window.shippingCalcStrings?.availableRates || 'Shipping options to your address:';
    const sorted = [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

    const html = `
      <div class="shipping-calculator__rates-header">${label}</div>
      <ul class="shipping-calculator__rates-list" role="list">
        ${sorted.map((rate, i) => this._rateItemHTML(rate, i)).join('')}
      </ul>
    `;
    this.results.innerHTML = html;
    this.results.classList.add('shipping-calculator__results--visible');
    this._isSelfMutating = false;
  }

  _rateItemHTML(rate, index) {
    const price  = parseFloat(rate.price);
    const isFree = price === 0;
    const priceStr = isFree
      ? (window.shippingCalcStrings?.free || 'Free')
      : this._formatMoney(price);

    return `
      <li class="shipping-calculator__rate-item ${index === 0 ? 'shipping-calculator__rate-item--cheapest' : ''}">
        <span class="shipping-calculator__rate-row">
          <span class="shipping-calculator__rate-name">${rate.name}&nbsp;</span>
          <span class="shipping-calculator__rate-price ${isFree ? 'shipping-calculator__rate-price--free' : ''}">${priceStr}</span>
        </span>
      </li>
    `;
  }

  _showNoRates() {
    if (!this.results) return;
    this._isSelfMutating = true;
    this.results.innerHTML = `
      <div class="shipping-calculator__notice shipping-calculator__notice--info">
        <span class="shipping-calculator__notice-icon">ℹ</span>
        <span>${window.shippingCalcStrings?.noRates || 'No shipping rates available for this address.'}</span>
      </div>
    `;
    this.results.classList.add('shipping-calculator__results--visible');
    this._isSelfMutating = false;
  }

  _showError(message) {
    if (!this.results) return;
    this._isSelfMutating = true;
    this.results.innerHTML = `
      <div class="shipping-calculator__notice shipping-calculator__notice--error" role="alert">
        <span class="shipping-calculator__notice-icon">!</span>
        <span>${message}</span>
      </div>
    `;
    this.results.classList.add('shipping-calculator__results--visible');
    this._isSelfMutating = false;
  }

  _clearResults() {
    if (!this.results) return;
    this._isSelfMutating = true;
    this.results.innerHTML = '';
    this.results.classList.remove('shipping-calculator__results--visible');
    this._isSelfMutating = false;
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  _setLoading(loading) {
    if (!this.submitBtn || !this.submitLabel || !this.submitSpinner) return;
    this.submitBtn.disabled = loading;
    this.submitLabel.hidden = loading;
    this.submitSpinner.hidden = !loading;
    if (loading) {
      this.submitSpinner.removeAttribute('aria-hidden');
    } else {
      this.submitSpinner.setAttribute('aria-hidden', 'true');
    }
  }

  _formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return `${currency} ${parseFloat(amount).toFixed(2)}`;
    }
  }

  _saveAddress(addr) {
    try {
      sessionStorage.setItem('sc_address', JSON.stringify(addr));
    } catch (_) {}
  }

  _restoreAddress() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem('sc_address') || 'null');
    } catch (_) {
      saved = null;
    }

    const countryEl = this.countryEl;
    if (!countryEl) return;

    if (saved && saved.country && this._hasOption(countryEl, saved.country)) {
      countryEl.value = saved.country;
      this._onCountryChange(saved.province);

      const zipEl = this.zipEl;
      if (zipEl && saved.zip) zipEl.value = saved.zip;
    } else {
      this._onCountryChange();
    }
  }
}

customElements.define('shipping-calculator', ShippingCalculator);